import { createPersistedStore, type PersistedStoreHook } from '@tasama/utils';
import type { BaseStoreState } from '@tasama/utils';
import type { ProjectDetailsFormValues } from '../schemas/projectDetails.schema';

/**
 * Thin coordinator store for ProjectDetails.
 *
 * The full validation logic has moved to react-hook-form inside ProjectDetailsPage.tsx.
 * This store exists only to:
 *  - Expose isValid to use-step-validation.hook.ts (stepper gating)
 *  - Expose formData to use-onboarding-payload.hook.ts (API payload building)
 *  - Expose reset() to store-manager.ts (clearing stores on company type change)
 */

interface ProjectDetailsState extends BaseStoreState {
  formData: ProjectDetailsFormValues;
  isValid: boolean;
  pendingApiError: { field: string; message: string } | null;
}

export interface ProjectDetailsStore extends ProjectDetailsState {
  setValidity: (_isValid: boolean) => void;
  setFormData: (_data: Partial<ProjectDetailsFormValues>) => void;
  setFieldError: (_field: string, _message: string) => void;
  clearApiError: () => void;
  reset: () => void;
}

const initialFormData: ProjectDetailsFormValues = {
  projectCodeName: '',
  projectDetails: '',
  expectedLaunchDate: '',
};

export const useProjectDetailsStore = createPersistedStore<ProjectDetailsState>(
  'project-details',
  {
    formData: initialFormData,
    isValid: false,
    pendingApiError: null,
  },
  (set) => ({
    setValidity: (isValid: boolean) => set({ isValid }),

    setFormData: (data: Partial<ProjectDetailsFormValues>) =>
      set((state: ProjectDetailsState) => ({
        formData: { ...state.formData, ...data },
      })),

    setFieldError: (field: string, message: string) =>
      set({ pendingApiError: { field, message } }),

    clearApiError: () => set({ pendingApiError: null }),

    reset: () =>
      set({ formData: initialFormData, isValid: false, pendingApiError: null }),
  }),
  {
    partialize: (state: ProjectDetailsState) => ({
      formData: state.formData,
      isValid: state.isValid,
      // pendingApiError intentionally excluded — transient, not persisted
    }),
  }
) as unknown as PersistedStoreHook<ProjectDetailsStore>;

// Selectors — interfaces with use-step-validation and use-onboarding-payload unchanged
export const useProjectDetailsFormData = () =>
  useProjectDetailsStore((state: ProjectDetailsState) => state.formData);
export const useProjectDetailsValues = () =>
  useProjectDetailsStore((state: ProjectDetailsState) => state.formData);
export const useProjectDetailsErrors = () =>
  ({}) as Partial<Record<keyof ProjectDetailsFormValues, string>>;
export const useProjectDetailsIsValid = () =>
  useProjectDetailsStore((state: ProjectDetailsState) => state.isValid);
